/**
 * Núcleo das ANOTAÇÕES sobre imagem — compartilhado pelo `web/` e pelo
 * `plugins/relator/`.
 *
 * Existe porque a mesma marca é criada num app e desenhada nos dois, e a
 * matemática que a posiciona é uma só. Antes deste arquivo havia duas cópias
 * (`plugins/relator/src/annotate/render.ts`, que emite string de SVG, e
 * `web/components/tarefas/ImagemAnotada.tsx`, que emite JSX) e o editor React
 * seria a terceira. Cópias de geometria não divergem com erro: divergem com a
 * marca alguns pixels fora do lugar, que ninguém nota até o cliente reclamar
 * que o pino aponta para o campo errado.
 *
 * **O que entra aqui:** o formato do dado e as funções PURAS de coordenada.
 * **O que NÃO entra:** renderização. Os dois apps desenham em tecnologias
 * incompatíveis — o widget concatena string dentro de um Shadow DOM fechado e
 * não pode ganhar React (bundle IIFE sem dependência de runtime), o front usa
 * JSX. Tentar unificar o desenho traria React para o bundle ou string para o
 * React; os dois são piores que manter dois renderers sobre um núcleo só.
 *
 * **Este arquivo é wire format.** As mesmas marcas viajam no multipart do
 * intake, são gravadas cruas em jsonb e voltam para três leitores. Acrescentar
 * um valor a `AnotacaoTipo` sem ensinar os dois renderers faz a marca sumir da
 * tela **em silêncio** — o renderer devolve vazio para tipo que não conhece.
 *
 * Sem dependências, e sem tocar em DOM: é o que permite viver fora dos dois
 * apps e ser testado sem browser.
 */

/** Formato da marca. Enum ON-THE-WIRE — ver o aviso no topo antes de estender. */
export type AnotacaoTipo = "pin" | "rect" | "arrow";

/**
 * Uma marca sobre a imagem, em coordenadas NORMALIZADAS (frações 0–1 relativas
 * à imagem, não ao contêiner). É o que torna a marca independente do tamanho em
 * que a imagem é exibida — a mesma anotação serve à miniatura, à lupa e ao
 * editor.
 *
 * `x2`/`y2` só existem em `rect` e `arrow` (o canto/ponta oposto). `pin` é um
 * ponto só.
 */
export interface Anotacao {
  tipo: AnotacaoTipo;
  x: number;
  y: number;
  x2?: number;
  y2?: number;
  comentario?: string;
}

/** Prende um número ao intervalo 0–1. */
export function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

/**
 * Caixa da IMAGEM na tela — não a do contêiner.
 *
 * A distinção é a armadilha central deste módulo: se a imagem for exibida menor
 * que a caixa medida (`object-fit: contain`, `max-height` na `<img>`, padding no
 * wrapper, ou `line-height` diferente de zero, que deixa uma faixa de baseline),
 * TODA marca sai deslocada de uma vez — sem erro, sem log, sem teste vermelho.
 * Quem chama tem de medir o elemento que encosta na borda do pixel.
 */
export interface Retangulo {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Ponto do cliente (coords de viewport) → coords normalizadas 0–1 da imagem. */
export function normalizarPonto(
  clientX: number,
  clientY: number,
  rect: Retangulo,
): { x: number; y: number } {
  const x = rect.width ? (clientX - rect.left) / rect.width : 0;
  const y = rect.height ? (clientY - rect.top) / rect.height : 0;
  return { x: clamp01(x), y: clamp01(y) };
}

/**
 * Descarta shapes degeneradas: `rect`/`arrow` com início ≈ fim viram ruído.
 *
 * O limiar é em fração da imagem (0.5%), não em pixel, porque é nessa unidade
 * que a marca vive — em pixel o mesmo gesto passaria numa tela e seria
 * descartado noutra.
 */
export function anotacaoValida(a: Anotacao): boolean {
  if (a.tipo === "pin") return true;
  if (a.x2 == null || a.y2 == null) return false;
  return Math.abs(a.x2 - a.x) > 0.005 || Math.abs(a.y2 - a.y) > 0.005;
}
